import { FallbackError } from "@core/domain/errors/fallback.error";
import { ValidationError } from "@core/domain/errors/validation.error";
import { Either } from "@core/domain/types/either";
import { inject, injectable } from "inversify";
import { BudgetTemplateDataSource } from "@project/data/datasource/budget-template.datasource";
import { BudgetTemplatesMapper } from "@project/data/mappers/budget-template/budget-template.mapper";
import { CreateBudgetFromTemplateMapper } from "@project/data/mappers/budget-template/create-budget-from-template.mapper";
import { MarkAsTemplateMapper } from "@project/data/mappers/budget-template/mark-as-template.mapper";
import { FinancialEntityBudgetMapper } from "@project/data/mappers/budget/financial-entity-budget.mapper";
import { FinancialEntityBudget } from "@project/domain/models/budget/financial-entity-budget.model";
import { BudgetTemplate } from "@project/domain/models/budget-template/budget-template";
import { CreateBudgetFromTemplate } from "@project/domain/models/budget-template/create-budget-from-template";
import { MarkAsTemplate } from "@project/domain/models/budget-template/mark-as-template";
import { BudgetTemplateRepositoryAbstract } from "@project/domain/repositories/budget-template.repository.abstract";

@injectable()
export class BudgetTemplateRepository extends BudgetTemplateRepositoryAbstract {
    constructor(
        @inject(BudgetTemplateDataSource)
        private readonly budgetTemplateDataSource: BudgetTemplateDataSource,
        @inject(BudgetTemplatesMapper)
        private readonly budgetTemplatesMapper: BudgetTemplatesMapper,
        @inject(CreateBudgetFromTemplateMapper)
        private readonly createBudgetFromTemplateMapper: CreateBudgetFromTemplateMapper,
        @inject(MarkAsTemplateMapper)
        private readonly markAsTemplateMapper: MarkAsTemplateMapper,
        @inject(FinancialEntityBudgetMapper)
        private readonly financialEntityBudgetMapper: FinancialEntityBudgetMapper,
    ) {
        super();
    }

    async getAllTemplates(): Promise<Either<FallbackError, BudgetTemplate[]>> {
        const result = await this.budgetTemplateDataSource.getAllTemplates();
        if (result.isLeft()) {
            return Either.Left(new FallbackError());
        }
        const data = result.right || [];
        const mappedData = this.budgetTemplatesMapper.map(data);
        return Either.Right(mappedData || []);
    }

    async createBudgetFromTemplate(createBudgetFromTemplate: CreateBudgetFromTemplate): Promise<Either<ValidationError | FallbackError, FinancialEntityBudget>> {
        const mappedData = this.createBudgetFromTemplateMapper.map(createBudgetFromTemplate);
        const result = await this.budgetTemplateDataSource.createBudgetFromTemplate(mappedData);
        if (result.isLeft()) {
            return Either.Left(result.getLeftOrThrow());
        }
        const mapped = this.financialEntityBudgetMapper.map(result.right);
        if (!mapped) {
            return Either.Left(new FallbackError());
        }
        return Either.Right(mapped);
    }

    async markAsTemplate(markAsTemplate: MarkAsTemplate): Promise<Either<ValidationError | FallbackError, FinancialEntityBudget>> {
        const mappedData = this.markAsTemplateMapper.map(markAsTemplate);
        const result = await this.budgetTemplateDataSource.markAsTemplate(mappedData);
        if (result.isLeft()) {
            return Either.Left(result.getLeftOrThrow());
        }
        const mapped = this.financialEntityBudgetMapper.map(result.right);
        if (!mapped) {
            return Either.Left(new FallbackError());
        }
        return Either.Right(mapped);
    }

    async createTemplate(templateData: { name: string; description: string; is_template: boolean; finantial_entity: null }): Promise<Either<ValidationError | FallbackError, FinancialEntityBudget>> {
        const result = await this.budgetTemplateDataSource.createTemplate(templateData);
        if (result.isLeft()) {
            return Either.Left(result.getLeftOrThrow());
        }
        const mapped = this.financialEntityBudgetMapper.map(result.right);
        if (!mapped) {
            return Either.Left(new FallbackError());
        }
        return Either.Right(mapped);
    }

    async createTemplateFromTemplate(templateId: number, name: string, description: string): Promise<Either<ValidationError | FallbackError, FinancialEntityBudget>> {
        const result = await this.budgetTemplateDataSource.createTemplateFromTemplate({ template_id: templateId, name, description });
        if (result.isLeft()) {
            return Either.Left(result.getLeftOrThrow());
        }
        const mapped = this.financialEntityBudgetMapper.map(result.right);
        if (!mapped) {
            return Either.Left(new FallbackError());
        }
        return Either.Right(mapped);
    }
}
